import React, { useState, useEffect, useMemo } from "react";
import { computeTTC, computeTaxAmount } from "../../data/taxRates";
import { FIXED_PRESETS, PERCENT_PRESETS } from "../../data/reductions";
import { adjustInventoryStock } from "../../data/inventory";
import { saveCatalog, generateInternalBarcode } from "../../data/catalog";

const fmt = (v) => `${Number(v || 0).toFixed(2)} €`;

const TVA_OPTIONS = [
  { value: 5, label: "5 %" },
  { value: 10, label: "10 %" },
  { value: 20, label: "20 %" },
];

const SpeedVenteModal = ({
  show,
  product,        // existing product object, or { barcode } for new product mode
  taxRates,
  catalog,
  onClose,
  onAddToCart,
  onUpdateCatalog,
}) => {
  const isNew = !product?.id;
  const [name, setName] = useState("");
  const [tvaRate, setTvaRate] = useState(10);
  const [priceHT, setPriceHT] = useState(product?.price || 0);
  const [quantity, setQuantity] = useState(1);
  const [remiseType, setRemiseType] = useState("none");
  const [remiseValue, setRemiseValue] = useState("");
  const [showStockAction, setShowStockAction] = useState(false);
  const [stockAddQty, setStockAddQty] = useState("");
  const [toast, setToast] = useState(null);

  const productTvaRateId = product?.tax_rate_id || "";
  const defaultRate = useMemo(() => {
    const r = taxRates.find((t) => t.id === productTvaRateId);
    return r?.rate ?? 10;
  }, [productTvaRateId, taxRates]);

  useEffect(() => {
    if (show && product) {
      setName(isNew ? "" : product.name || "");
      setTvaRate(defaultRate);
      setPriceHT(Number(product.price) || 0);
      setQuantity(1);
      setRemiseType("none");
      setRemiseValue("");
      setShowStockAction(false);
      setStockAddQty("");
      setToast(null);
    }
  }, [show, product, defaultRate, isNew]);

  if (!show || !product) return null;

  const barcode = product.barcode || generateInternalBarcode();
  const ttcUnit = computeTTC(priceHT, tvaRate);
  const tvaAmountUnit = computeTaxAmount(priceHT, tvaRate);
  const remise = (() => {
    if (remiseType === "fixed") {
      const v = Math.min(parseFloat(remiseValue) || 0, ttcUnit);
      return Math.max(0, +v.toFixed(2));
    }
    if (remiseType === "percent") {
      const pct = Math.min(Math.max(parseFloat(remiseValue) || 0, 0), 100);
      return +(ttcUnit * pct / 100).toFixed(2);
    }
    return 0;
  })();
  const unitPriceFinal = +(ttcUnit - remise).toFixed(2);
  const lineHT = +(priceHT * quantity).toFixed(2);
  const lineTVA = +(tvaAmountUnit * quantity).toFixed(2);
  const lineTotal = +(unitPriceFinal * quantity).toFixed(2);

  const handleAddToCart = async () => {
    let finalProduct = product;

    if (isNew) {
      if (!name.trim()) { setToast("Veuillez saisir un nom."); return; }
      const newProduct = {
        id: `quick-${Date.now()}`,
        name: name.trim(),
        barcode,
        price: Number(priceHT) || 0,
        stock: 0,
        sku: `SKU-${Date.now()}`,
        category: "Caisse",
        type: "regular",
        saleType: "unit",
        unitType: "piece",
        tax_rate_id: taxRates.find((t) => t.rate === tvaRate)?.id || "",
        isIncomplete: true,
      };
      const updatedCatalog = [newProduct, ...catalog];
      onUpdateCatalog(updatedCatalog);
      await saveCatalog(updatedCatalog);
      finalProduct = newProduct;
    }

    onAddToCart({
      product: finalProduct,
      quantity,
      priceHT: Number(priceHT) || 0,
      tvaRate,
      tvaAmountUnit,
      remiseType,
      remiseValue: remiseType === "none" ? 0 : parseFloat(remiseValue) || 0,
      unitPriceFinal,
      lineTotal,
      lineHT,
      lineTVA,
    });
    onClose();
  };

  const handleAddStock = async () => {
    const qty = parseInt(stockAddQty, 10);
    if (!qty || qty <= 0 || isNew) return;
    try {
      await adjustInventoryStock(product.id, qty, "Ajout rapide caisse");
      const updated = catalog.map((p) =>
        p.id === product.id ? { ...p, stock: (p.stock || 0) + qty } : p
      );
      onUpdateCatalog(updated);
      setStockAddQty("");
      setToast(`+${qty} unités ajoutées au stock`);
      setTimeout(() => setToast(null), 3000);
    } catch (e) {
      setToast("Erreur de mise à jour du stock");
      setTimeout(() => setToast(null), 3000);
    }
  };

  const handleUpdatePrice = async () => {
    if (isNew) return;
    const newHT = parseFloat(priceHT);
    if (isNaN(newHT) || newHT < 0) return;
    const updated = catalog.map((p) =>
      p.id === product.id ? { ...p, price: newHT } : p
    );
    onUpdateCatalog(updated);
    await saveCatalog(updated);
    setToast("Prix mis à jour");
    setTimeout(() => setToast(null), 3000);
  };

  return (
    <div className="modal show d-block" style={{ backgroundColor: "rgba(0,0,0,0.75)", zIndex: 1070 }}>
      <div className="modal-dialog modal-dialog-centered" style={{ maxWidth: "480px" }}>
        <div className="modal-content border-0 shadow-lg" style={{ borderRadius: "12px", overflow: "hidden" }}>

          {/* Header */}
          <div className="modal-header border-0 pb-0 pt-3 px-4">
            <div className="d-flex align-items-center gap-3">
              {!isNew && (
                <img
                  src={product.imageUrl || "/placeholder.png"}
                  alt={product.name}
                  style={{ width: "56px", height: "56px", objectFit: "cover", borderRadius: "8px" }}
                  onError={(e) => { e.target.src = "https://via.placeholder.com/56"; }}
                />
              )}
              <div>
                <h6 className="fw-bold mb-0">
                  {isNew ? <span className="text-warning"><i className="bi bi-plus-circle me-1"></i>Nouveau produit</span> : product.name}
                </h6>
                <small className="text-muted">Barcode : {barcode}</small>
              </div>
            </div>
            <button type="button" className="btn-close" onClick={onClose}></button>
          </div>

          <div className="modal-body px-4 pb-4 pt-2">

            {/* New product name input */}
            {isNew && (
              <div className="mb-3">
                <label className="form-label small fw-bold text-muted">Nom du produit</label>
                <input
                  type="text"
                  className="form-control"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Nom du produit..."
                  autoFocus
                />
              </div>
            )}

            {/* Current stock (existing only) */}
            {!isNew && (
              <div className="d-flex align-items-center justify-content-between mb-3" style={{ fontSize: "0.85rem" }}>
                <span className="text-muted">
                  Stock actuel : <strong>{product.stock ?? "—"}</strong>
                </span>
                <span className="text-muted">
                  Réf : {product.sku || "—"}
                </span>
              </div>
            )}

            {/* Price HT + TVA */}
            <div className="row g-2 mb-3">
              <div className="col-5">
                <label className="form-label small fw-bold text-muted">Prix HT (€)</label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  className="form-control"
                  value={priceHT}
                  onChange={(e) => setPriceHT(e.target.value)}
                />
              </div>
              <div className="col-7">
                <label className="form-label small fw-bold text-muted">TVA</label>
                <div className="d-flex gap-1">
                  {TVA_OPTIONS.map((opt) => (
                    <button
                      key={opt.value}
                      type="button"
                      className={`btn btn-sm flex-grow-1 ${tvaRate === opt.value ? "btn-dark text-white" : "btn-outline-secondary"}`}
                      onClick={() => setTvaRate(opt.value)}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {/* Computed TTC display */}
            <div className="bg-light rounded-3 p-3 mb-3">
              <div className="d-flex justify-content-between" style={{ fontSize: "0.85rem" }}>
                <span className="text-muted">TTC unitaire</span>
                <strong>{fmt(ttcUnit)}</strong>
              </div>
              <div className="d-flex justify-content-between" style={{ fontSize: "0.85rem" }}>
                <span className="text-muted">TVA ({tvaRate}%)</span>
                <span>{fmt(tvaAmountUnit)}</span>
              </div>
            </div>

            {/* Remise */}
            <div className="mb-3">
              <label className="form-label small fw-bold text-muted mb-1">Remise</label>
              <div className="d-flex gap-1 mb-2">
                {[
                  { id: "none", label: "Aucune" },
                  { id: "fixed", label: "€ fixe" },
                  { id: "percent", label: "% / %" },
                ].map((r) => (
                  <button
                    key={r.id}
                    type="button"
                    className={`btn btn-sm flex-grow-1 ${remiseType === r.id ? "btn-success text-white" : "btn-outline-success"}`}
                    onClick={() => setRemiseType(r.id)}
                  >
                    {r.label}
                  </button>
                ))}
              </div>
              {remiseType !== "none" && (
                <div className="d-flex gap-1 flex-wrap">
                  {remiseType === "fixed" ? FIXED_PRESETS.map((p) => (
                    <button key={p} type="button" className={`btn btn-sm ${remiseValue === String(p) ? "btn-success text-white" : "btn-outline-success"}`} onClick={() => setRemiseValue(String(p))}>{p} €</button>
                  )) : PERCENT_PRESETS.map((p) => (
                    <button key={p} type="button" className={`btn btn-sm ${remiseValue === String(p) ? "btn-success text-white" : "btn-outline-success"}`} onClick={() => setRemiseValue(String(p))}>{p} %</button>
                  ))}
                  <input
                    type="number"
                    step={remiseType === "fixed" ? "0.01" : "1"}
                    min="0"
                    className="form-control form-control-sm"
                    style={{ width: "70px" }}
                    placeholder={remiseType === "fixed" ? "€" : "%"}
                    value={remiseValue}
                    onChange={(e) => setRemiseValue(e.target.value)}
                  />
                </div>
              )}
              {remise > 0 && (
                <div className="small text-success fw-bold mt-1">Remise unitaire : -{fmt(remise)}</div>
              )}
            </div>

            {/* Quantity + Total */}
            <div className="d-flex align-items-center justify-content-between mb-3 p-2 rounded" style={{ backgroundColor: "#f8f9fa" }}>
              <div className="d-flex align-items-center gap-2">
                <label className="small fw-bold text-muted mb-0">Qté</label>
                <button className="btn btn-sm btn-outline-secondary" onClick={() => setQuantity(Math.max(1, quantity - 1))}>-</button>
                <input
                  type="number"
                  min="1"
                  className="form-control form-control-sm text-center fw-bold"
                  style={{ width: "55px" }}
                  value={quantity}
                  onChange={(e) => setQuantity(Math.max(1, parseInt(e.target.value) || 1))}
                />
                <button className="btn btn-sm btn-outline-secondary" onClick={() => setQuantity(quantity + 1)}>+</button>
              </div>
              <div className="text-end">
                <div style={{ fontSize: "0.8rem", color: "#666" }}>HT {fmt(lineHT)} + TVA {fmt(lineTVA)}</div>
                <div className="fw-bold fs-5" style={{ color: "#166534" }}>{fmt(lineTotal)}</div>
              </div>
            </div>

            {/* Stock + Price actions (existing only) */}
            {!isNew && (
              <div className="d-flex gap-2 mb-3">
                <button
                  type="button"
                  className={`btn btn-sm ${showStockAction ? "btn-warning text-dark" : "btn-outline-warning"}`}
                  onClick={() => setShowStockAction(!showStockAction)}
                >
                  <i className="bi bi-box-seam me-1"></i>Ajouter stock
                </button>
                <button
                  type="button"
                  className="btn btn-sm btn-outline-info"
                  onClick={handleUpdatePrice}
                >
                  <i className="bi bi-pencil me-1"></i>Enregistrer prix HT
                </button>
              </div>
            )}
            {showStockAction && !isNew && (
              <div className="d-flex gap-2 mb-3">
                <input
                  type="number"
                  min="1"
                  className="form-control form-control-sm"
                  placeholder="Quantité à ajouter"
                  value={stockAddQty}
                  onChange={(e) => setStockAddQty(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && handleAddStock()}
                />
                <button className="btn btn-sm btn-warning text-dark fw-bold" onClick={handleAddStock}>+ Ajouter</button>
              </div>
            )}
            {toast && <div className="alert alert-success py-2 px-3 mb-2" style={{ fontSize: "0.8rem" }}>{toast}</div>}

            {/* Add to ticket */}
            <button
              className="btn w-100 fw-bold py-2 fs-6"
              style={{ backgroundColor: "#16a34a", color: "white" }}
              onClick={handleAddToCart}
            >
              <i className="bi bi-cart-plus me-2"></i>{isNew ? "CRÉER + AJOUTER AU TICKET" : "AJOUTER AU TICKET"} — {fmt(lineTotal)}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SpeedVenteModal;